import React, { useMemo, useState } from "react";
import {
  AlertTriangle,
  ChevronUp,
  Download,
  FileText,
  MousePointer2,
  Trash2,
  X,
} from "lucide-react";
import { safeIcon } from "../config/graphIcons";

const formatLabel = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .toLowerCase();

const getNodeDocumentId = (node) =>
  node?.document_id || node?.doc_id || node?.cardId || null;

const buildClusterLabel = (nodes) => {
  const names = (nodes || [])
    .map((node) => node?.name || node?.id)
    .filter(Boolean)
    .slice(0, 3);

  if (!names.length) return "Untitled cluster";
  return names.join(" / ");
};

const SelectedItemsPanel = ({
  dark,
  t,
  selectedNodes = [],
  onRemoveNode,
  onClearSelection,
  onDownloadSelection,
  isDownloadingSelection = false,
  onDeleteNode,
  onDeleteSelected,
  isDeletingSelection = false,
}) => {
  const [open, setOpen] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const selectedClusters = useMemo(() => {
    const byKey = new Map();

    selectedNodes.forEach((node) => {
      if (!node) return;
      const documentId = getNodeDocumentId(node);
      const key = documentId || `node:${node.id || node.name}`;

      if (!byKey.has(key)) {
        byKey.set(key, {
          key,
          documentId,
          nodes: [],
          representative: node,
        });
      }

      byKey.get(key).nodes.push(node);
    });

    return Array.from(byKey.values()).map((cluster) => {
      const first = cluster.representative || cluster.nodes[0] || {};
      return {
        ...cluster,
        label: buildClusterLabel(cluster.nodes),
        detail: cluster.documentId
          ? `${cluster.nodes.length} nodes`
          : `${cluster.nodes.length} nodes`,
        icon: first.icon || first.group || "document",
        representative: first,
      };
    });
  }, [selectedNodes]);

  const nodeCount = selectedNodes.length;
  const clusterCount = selectedClusters.length;
  const MAX_VISIBLE_ITEMS = 100;
  const visibleItems = selectedClusters.slice(0, MAX_VISIBLE_ITEMS);
  const hiddenItemCount = Math.max(
    0,
    selectedClusters.length - MAX_VISIBLE_ITEMS,
  );

  if (!nodeCount) return null;

  const panel = {
    background: dark ? "rgba(10,16,28,0.72)" : "rgba(255,255,255,0.78)",
    border: dark
      ? "1px solid rgba(148,163,184,0.14)"
      : "1px solid rgba(100,116,139,0.18)",
    boxShadow: dark
      ? "0 18px 48px rgba(0,0,0,0.26), inset 0 1px 0 rgba(255,255,255,0.04)"
      : "0 18px 42px rgba(15,23,42,0.10), inset 0 1px 0 rgba(255,255,255,0.70)",
  };

  const text = {
    primary: dark ? "rgba(226,232,240,0.90)" : "rgba(15,23,42,0.88)",
    muted: dark ? "rgba(148,163,184,0.62)" : "rgba(71,85,105,0.68)",
    faint: dark ? "rgba(148,163,184,0.35)" : "rgba(71,85,105,0.42)",
    accent: dark ? "rgba(56,189,248,0.92)" : "rgba(2,132,199,0.95)",
    danger: dark ? "rgba(248,113,113,0.86)" : "rgba(220,38,38,0.86)",
    dangerStrong: dark ? "rgba(254,202,202,0.94)" : "rgba(153,27,27,0.95)",
  };

  const inactiveHover = dark
    ? "rgba(148,163,184,0.06)"
    : "rgba(100,116,139,0.07)";

  const dangerBg = dark ? "rgba(239,68,68,0.10)" : "rgba(239,68,68,0.08)";
  const dangerBgHover = dark ? "rgba(239,68,68,0.16)" : "rgba(239,68,68,0.13)";
  const dangerBorder = dark
    ? "1px solid rgba(239,68,68,0.22)"
    : "1px solid rgba(239,68,68,0.18)";

  const runDelete = async () => {
    if (!confirmDelete) return;

    if (confirmDelete.type === "single" && confirmDelete.cluster) {
      await onDeleteNode?.(confirmDelete.cluster.nodes[0]);
    }

    if (confirmDelete.type === "bulk") {
      await onDeleteSelected?.();
    }

    setConfirmDelete(null);
  };

  const confirmTitle =
    confirmDelete?.type === "single"
      ? `Delete “${confirmDelete.cluster?.label || "selected cluster"}”?`
      : `Delete ${clusterCount} selected document${clusterCount === 1 ? "" : "s"}?`;

  return (
    <div
      className="pointer-events-auto absolute right-4 top-[76px] z-20 w-[332px] overflow-hidden rounded-xl backdrop-blur-xl"
      style={panel}
    >
      <button
        className="flex w-full cursor-pointer items-center justify-between px-3.5 py-2.5 text-left transition-colors"
        style={{
          borderBottom: open
            ? dark
              ? "1px solid rgba(148,163,184,0.10)"
              : "1px solid rgba(100,116,139,0.12)"
            : "1px solid transparent",
        }}
        onClick={() => setOpen((prev) => !prev)}
      >
        <div className="flex min-w-0 items-center gap-2">
          <div
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg"
            style={{
              background: dark
                ? "rgba(14,165,233,0.10)"
                : "rgba(14,165,233,0.08)",
              border: dark
                ? "1px solid rgba(14,165,233,0.18)"
                : "1px solid rgba(14,165,233,0.14)",
            }}
          >
            <MousePointer2 size={13} style={{ color: text.accent }} />
          </div>

          <div className="min-w-0">
            <div
              className="truncate text-[11px] font-medium uppercase tracking-[0.11em]"
              style={{ color: text.primary }}
            >
              Selected Nodes
            </div>
            <div className="text-[10px]" style={{ color: text.faint }}>
              {nodeCount} nodes
            </div>
          </div>
        </div>

        <ChevronUp
          size={14}
          style={{
            color: text.muted,
            transform: open ? "rotate(0deg)" : "rotate(180deg)",
            transition: "transform 0.18s ease",
          }}
        />
      </button>

      {open && (
        <>
          {confirmDelete && (
            <div
              className="mx-2 mt-2 rounded-lg px-3 py-2.5"
              style={{
                background: dangerBg,
                border: dangerBorder,
              }}
            >
              <div className="flex items-start gap-2">
                <AlertTriangle
                  size={14}
                  className="mt-0.5 shrink-0"
                  style={{ color: text.danger }}
                />
                <div className="min-w-0 flex-1">
                  <div
                    className="text-[11px] font-medium"
                    style={{ color: text.dangerStrong }}
                  >
                    {confirmTitle}
                  </div>
                  <div
                    className="mt-1 text-[10px] leading-snug"
                    style={{ color: text.muted }}
                  >
                    This deletes by document ID, so the whole selected node
                    cluster will be removed from the graph.
                  </div>
                </div>
              </div>

              <div className="mt-2.5 grid grid-cols-2 gap-1.5">
                <button
                  className="rounded-md px-2 py-1.5 text-[11px] transition-colors"
                  style={{
                    color: text.muted,
                    background: dark
                      ? "rgba(148,163,184,0.06)"
                      : "rgba(100,116,139,0.06)",
                    border: dark
                      ? "1px solid rgba(148,163,184,0.10)"
                      : "1px solid rgba(100,116,139,0.12)",
                  }}
                  onClick={() => setConfirmDelete(null)}
                  disabled={isDeletingSelection}
                >
                  Cancel
                </button>

                <button
                  className="rounded-md px-2 py-1.5 text-[11px] transition-colors"
                  style={{
                    color: text.dangerStrong,
                    background: dangerBgHover,
                    border: dangerBorder,
                    cursor: isDeletingSelection ? "wait" : "pointer",
                    opacity: isDeletingSelection ? 0.68 : 1,
                  }}
                  onClick={runDelete}
                  disabled={isDeletingSelection}
                >
                  {isDeletingSelection ? "Deleting…" : "Delete"}
                </button>
              </div>
            </div>
          )}

          <div
            className="relative max-h-[min(360px,44vh)] overflow-y-auto px-1.5 py-1.5"
            style={{ scrollbarWidth: "thin" }}
          >
            {visibleItems.map((cluster) => {
              const itemId = cluster.representative?.id || cluster.key;
              const IconComp = safeIcon(cluster.icon) || FileText;
              const iconColor =
                t.node.typeColors?.[cluster.icon] || t.node.iconColor;

              return (
                <div
                  key={`cluster-${cluster.key}`}
                  className="group flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-all"
                  style={{ border: "1px solid transparent" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = inactiveHover;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  <div
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition-colors"
                    style={{
                      background: dark
                        ? "rgba(148,163,184,0.06)"
                        : "rgba(100,116,139,0.06)",
                    }}
                  >
                    {React.createElement(IconComp, {
                      size: 13,
                      strokeWidth: 1.9,
                      style: {
                        color: iconColor,
                        opacity: 0.9,
                        flexShrink: 0,
                      },
                    })}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div
                      className="truncate text-[12px] font-normal"
                      style={{ color: text.primary }}
                    >
                      {cluster.label}
                    </div>
                    <div
                      className="truncate text-[10px]"
                      style={{ color: text.faint }}
                    >
                      {cluster.documentId
                        ? `${cluster.nodes.length} nodes`
                        : formatLabel(cluster.detail)}
                    </div>
                  </div>

                  <div
                    className="flex shrink-0 items-center gap-1 border-l pl-1.5"
                    style={{
                      borderColor: dark
                        ? "rgba(148,163,184,0.10)"
                        : "rgba(100,116,139,0.12)",
                    }}
                  >
                    <button
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition-colors"
                      style={{
                        color: text.faint,
                        background: "transparent",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveNode(itemId);
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = text.accent;
                        e.currentTarget.style.background = dark
                          ? "rgba(14,165,233,0.10)"
                          : "rgba(14,165,233,0.08)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = text.faint;
                        e.currentTarget.style.background = "transparent";
                      }}
                      title="Remove document from selection"
                    >
                      <X size={12} />
                    </button>

                    <button
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition-colors"
                      style={{
                        color: text.danger,
                        background: "transparent",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmDelete({ type: "single", cluster });
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = text.dangerStrong;
                        e.currentTarget.style.background = dangerBg;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = text.danger;
                        e.currentTarget.style.background = "transparent";
                      }}
                      title="Delete document cluster"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              );
            })}

            {hiddenItemCount > 0 && (
              <div
                className="px-3 py-2 text-center text-[10px]"
                style={{ color: text.faint }}
              >
                Showing first {MAX_VISIBLE_ITEMS} of {selectedClusters.length}{" "}
                documents.
              </div>
            )}
          </div>

          <div
            className="grid grid-cols-2"
            style={{
              borderTop: dark
                ? "1px solid rgba(148,163,184,0.10)"
                : "1px solid rgba(100,116,139,0.12)",
            }}
          >
            <button
              className="px-3 py-2 text-center text-[11px] font-normal transition-colors"
              style={{
                color: text.accent,
                background: "transparent",
                borderRight: dark
                  ? "1px solid rgba(148,163,184,0.10)"
                  : "1px solid rgba(100,116,139,0.12)",
                cursor: "pointer",
              }}
              onClick={onClearSelection}
            >
              Clear selected
            </button>

            <button
              className="flex items-center justify-center gap-1.5 px-3 py-2 text-center text-[11px] font-normal transition-colors"
              style={{
                color: text.accent,
                background: "transparent",
                cursor: isDownloadingSelection ? "wait" : "pointer",
                opacity: isDownloadingSelection ? 0.68 : 1,
              }}
              onClick={onDownloadSelection}
              disabled={isDownloadingSelection}
            >
              <Download size={11} />
              {isDownloadingSelection ? "Downloading…" : "Download JSON"}
            </button>
          </div>

          <button
            className="flex w-full items-center justify-center gap-1.5 px-3 py-2 text-[11px] font-normal transition-colors"
            style={{
              color: text.danger,
              background: dark
                ? "rgba(239,68,68,0.06)"
                : "rgba(239,68,68,0.05)",
              borderTop: dark
                ? "1px solid rgba(239,68,68,0.14)"
                : "1px solid rgba(239,68,68,0.12)",
              cursor: isDeletingSelection ? "wait" : "pointer",
              opacity: isDeletingSelection ? 0.68 : 1,
            }}
            onClick={() => setConfirmDelete({ type: "bulk" })}
            disabled={isDeletingSelection}
          >
            <Trash2 size={11} />
            {isDeletingSelection
              ? "Deleting…"
              : `Delete selected (${clusterCount} nodes)`}
          </button>
        </>
      )}
    </div>
  );
};

export default SelectedItemsPanel;
